import React, { FC, Fragment } from 'react';
import {
  Grid,
  Switch,
  Typography,
  List,
  ListItem,
  Divider,
  Select,
  MenuItem,
  Box,
  Input,
} from '@mui/material';
import { useParams } from 'react-router-dom';
import CentsLoader from 'components/common/centsLoader/CentsLoader';
import { useBusinessSettings } from 'hooks/business/useBusinessSettings';
import { Store } from 'types/store';
import styles from './FeatureFlags.module.scss';

const IndividualBusinessSettingsFlags: FC<{ storeList: Store[] }> = ({ storeList }) => {
  const { id } = useParams();
  const {
    settings,
    error,
    business,
    isLoadingBusiness,
    isLoadingTransportationPreference,
    saveIndividualValueForBusinessSettings,
    gtmIdValue,
    handleGtmIdChange,
  } = useBusinessSettings({ id, storeList });

  if (isLoadingBusiness || isLoadingTransportationPreference) {
    return <CentsLoader />;
  }

  return (
    <section className={styles.flagSection}>
      {error && <p className={styles.error}>{error}</p>}
      <p className={styles.flagSectionDescription}>
        These are permanent configuration flags. Their state is stored in our database.
      </p>
      <List>
        {settings.map(
          (
            { label, propertyName, description, dropdown, input, options, onChange, value },
            idx,
          ) => (
            <Fragment key={`business-setting-${propertyName}`}>
              <ListItem>
                <Grid container alignItems="center">
                  <Grid item xs={5}>
                    <Typography className={styles.flagLabel}>{label}</Typography>
                  </Grid>
                  <Grid item xs={5}>
                    <Typography variant="body2">{description}</Typography>
                  </Grid>
                  <Grid item xs={2}>
                    <Box display="flex" justifyContent="flex-end">
                      {dropdown && (
                        <Select
                          value={value || business?.settings?.deliveryTimingsPreference}
                          onChange={(event) => {
                            onChange?.(event.target.value) ||
                              saveIndividualValueForBusinessSettings.mutate({
                                field: propertyName,
                                value: event.target.value,
                              });
                          }}
                        >
                          {options.map((option) => (
                            <MenuItem key={option.value} value={option.value}>
                              {option.display}
                            </MenuItem>
                          ))}
                        </Select>
                      )}
                      {input && (
                        <Input
                          value={gtmIdValue ?? business?.settings?.gtmId}
                          onChange={(event) => {
                            handleGtmIdChange(propertyName!, event.target.value);
                          }}
                        />
                      )}
                      {!dropdown && !input && (
                        <Switch
                          checked={business?.settings?.[propertyName]}
                          onChange={() => {
                            saveIndividualValueForBusinessSettings.mutate({
                              field: propertyName,
                              value: !business?.settings?.[propertyName],
                            });
                          }}
                          color="primary"
                        />
                      )}
                    </Box>
                  </Grid>
                </Grid>
              </ListItem>
              {idx !== settings.length - 1 && <Divider />}
            </Fragment>
          ),
        )}
      </List>
    </section>
  );
};

export default IndividualBusinessSettingsFlags;
